import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { holdingsResponseSchema, type Assignment } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { StatusPill } from '../gear/GearCard.js';
import { ReturnForm } from '../gear/GearActions.js';
import { formatDate } from '../issuance/FormsPage.js';

const primary =
  'rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';

/**
 * The platoon store (owner, 2026-09-13, story 30): what the platoon sergeant
 * holds from the company store, how much of it is out with the platoon's
 * soldiers, and the way back — a soldier hands an item to the sergeant, the
 * sergeant confirms it here with a condition, and it is in hand again.
 * Returning to the CSM is the ordinary return on the sergeant's own gear
 * card, refused while anything is still out.
 */
export function PlatoonStorePage() {
  const { t, i18n } = useTranslation();
  const state = useApi('/distribution/holdings', holdingsResponseSchema);
  const [returning, setReturning] = useState<string | null>(null);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const { holdings, distributed, soldiers } = state.data;
  const name = (uid: string) => soldiers.find((s) => s.uid === uid)?.name ?? uid;
  const label = (a: Assignment) => (i18n.language.startsWith('en') ? a.nameEn : a.nameHe);
  const inHand = (a: Assignment) => a.quantity - a.expendedQuantity - a.distributedQuantity;
  const canDistribute = holdings.some((a) => inHand(a) > 0);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="store-holdings">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{t('store.title')}</h2>
            <p className="mt-1 text-sm text-slate-500">{t('store.intro')}</p>
          </div>
          <Link
            to="/platoon-store/distribute"
            className={`${primary} ${canDistribute ? '' : 'pointer-events-none opacity-40'}`}
            aria-disabled={!canDistribute}
            data-testid="store-distribute"
          >
            {t('store.distribute')}
          </Link>
        </div>

        <h3 className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">{t('store.holdings')}</h3>
        {holdings.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500" data-testid="store-empty">{t('store.noHoldings')}</p>
        ) : (
          <ul className="mt-1 divide-y divide-slate-100">
            {holdings.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="store-holding" data-id={a.id}>
                <span>
                  <span className="font-medium text-slate-900">{label(a)}</span>
                  {a.serialNumber && (
                    <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{a.serialNumber}</span>
                  )}
                </span>
                <span className="text-sm text-slate-600" data-testid="store-holding-counts">
                  <span className="latin-inline font-medium text-slate-900">{inHand(a)}</span> {t('store.inHand')}
                  {' · '}
                  <span className="latin-inline">{a.distributedQuantity}</span> {t('store.out')}
                  {' · '}
                  <span className="text-slate-400">{t('store.ofTotal', { total: a.quantity - a.expendedQuantity })}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="store-distributed">
        <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('store.distributed')}</h3>
        {distributed.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">{t('store.nothingOut')}</p>
        ) : (
          <ul className="mt-1 divide-y divide-slate-100">
            {distributed.map((a) => (
              <li key={a.id} className="py-2" data-testid="store-out" data-id={a.id} data-status={a.status}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="font-medium text-slate-900">{name(a.soldierUid)}</span>
                    <span className="ms-2 text-slate-700">{label(a)}</span>
                    {a.serialNumber ? (
                      <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{a.serialNumber}</span>
                    ) : (
                      <span className="ms-2 text-sm text-slate-500">× <span className="latin-inline">{a.quantity}</span></span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-slate-500">
                    <StatusPill status={a.status} />
                    {a.issuedAt && <span className="latin-inline">{formatDate(a.issuedAt, i18n.language)}</span>}
                    {a.status === 'pending_signature' && (
                      <Link to={`/forms/${a.formId}`} className="underline-offset-4 hover:underline">
                        {t('store.pendingSignature')}
                      </Link>
                    )}
                    {(a.status === 'active' || a.status === 'in_rotation') && !a.consumable && (
                      <button
                        type="button"
                        onClick={() => setReturning(returning === a.id ? null : a.id)}
                        className="rounded px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-100"
                        data-testid="store-confirm-return"
                      >
                        {t('store.confirmReturn')}
                      </button>
                    )}
                  </span>
                </div>
                {returning === a.id && (
                  <div className="mt-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
                    <ReturnForm
                      a={a}
                      onDone={() => {
                        setReturning(null);
                        state.reload();
                      }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
